import { routingUrls } from '@doltech/core/lib/routing/routing.config';
import { IconOtherClose } from '@doltech/icons/IconOtherClose';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { deviceBreakpoints } from '../../hooks/useDeviceQuery.hook';
import { useBannerScroll } from '../../hooks/useLandingHeaderDisplayAsHeadRoom';
import { useUserAuthentication } from '../Auth/hocs/withUserAuthentication';
import { colorsV2 } from '../colors-v2';
import { Typography } from '../DesignSystemV2/Typography';
import { Link } from '../Link';
import { useGetReferralCode } from './useGetReferralCode';
import { useGetAuthInfo } from '@doltech/core/lib/auth/withGlobalUserAuthInfo';

const BANNER_ID = 'referral-share-banner';

const Main = styled.div`
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  z-index: 200;
  background-color: ${colorsV2.blue20};

  &.expired {
    background-color: ${colorsV2.primary20};
  }

  transition: all 0.2s ease;

  &.closed {
    display: none;
  }

  &.hide {
    top: 0;
    opacity: 0;
    z-index: -1;
  }

  .container {
    display: grid;
    align-items: center;
    grid-template-columns: minmax(0, max-content) max-content 1fr;
    padding: 12px 16px;
    gap: 12px;
    margin: 0 auto;
  }

  .mobile-text {
    display: inline;
    @media ${deviceBreakpoints.fromTablet} {
      display: none;
    }
  }

  .desktop-text {
    display: none;
    @media ${deviceBreakpoints.fromTablet} {
      display: inline;
    }
  }

  button {
    box-shadow: none;
    outline: none;
    border: none;
  }

  .btn-close {
    font-size: 20px;
    line-height: 0;
    text-align: right;
    cursor: pointer;
  }

  .btn-share {
    background-color: ${colorsV2.blue100};
    color: ${colorsV2.white100};
    padding: 4px 12px;
    font-size: 14px;
    font-weight: 600;
    border-radius: 4px;
    line-height: 24px;
    cursor: pointer;

    display: grid;
    grid-auto-columns: max-content;
    grid-auto-flow: column;
    align-items: center;
    gap: 4px;

    .icon {
      line-height: 0;
      font-size: 20px;
    }

    @media ${deviceBreakpoints.fromTablet} {
      padding: 6px 12px;
    }
  }

  &.landing-daily-news {
    .container {
      @media (min-width: 541px) {
        padding: 12px 0;
        width: 508px;
      }
      @media (min-width: 1024px) {
        width: 992px;
      }
      @media (min-width: 1136px) {
        width: 1080px;
      }
    }
  }

  &.landing-home {
    .container {
      padding: 12px 24px;

      @media (min-width: 1194px) {
        padding: 12px 0;
        width: 1080px;
      }
    }
  }

  &.daily-news-detail {
    .container {
      @media (min-width: 541px) {
        padding: 12px 0;
        width: 576px;
      }
    }
  }

  &.landing-vocab,
  &.landing-grammar,
  &.book-seo-page,
  &.landing-layout-sub-listing,
  &.landing-layout-with-banner {
    .container {
      @media (min-width: 768px) {
        padding: 12px 0;
        width: 712px;
      }
      @media (min-width: 1280px) {
        width: 1080px;
      }
    }
  }

  &.landing-layout-with-seo-header,
  &.landing-ielts-sample-detail,
  &.blog-detail-layout {
    .container {
      @media (min-width: 768px) {
        padding: 12px 0;
        width: 712px;
      }
    }
  }

  &.landing-dictionary {
    .container {
      @media (min-width: 960px) {
        padding: 12px 0;
        width: 960px;
      }
      @media (min-width: 1366px) {
        width: 1078px;
      }
    }
  }
`;

const toggleReferralBannerClass = (elm, isBannerClosed) => {
  if (!elm) return;
  if (isBannerClosed) {
    elm.classList.remove('has-referral-banner');
    return;
  }
  elm.classList.add('has-referral-banner');
};

const Red = styled.span`
  color: ${colorsV2.primary100};
  font-weight: 700;
`;

const ActionBtn = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 12px;
  background: ${colorsV2.blue100};
  border-radius: 4px;
  color: ${colorsV2.white100};

  &.expired {
    background: ${colorsV2.primary100};
  }
`;

const ActionButtonA = () => {
  const { openLoginModal } = useUserAuthentication();

  return (
    <ActionBtn onClick={openLoginModal}>
      <Typography.Text variant="semi-bold/14" color="white100">
        Đăng ký
      </Typography.Text>
    </ActionBtn>
  );
};

const RegisterDOLCourseBannerWrapper = styled(Main)`
  background-color: ${colorsV2.primary10};

  .content {
    span {
      color: ${colorsV2.black80};
    }
  }
`;
const RegisterDOLCourseBanner = ({ className, closeBanner }) => {
  return (
    <RegisterDOLCourseBannerWrapper id={BANNER_ID} className={cl(BANNER_ID, 'hide', className)}>
      <div className="container">
        <div className={cl('content', 'expired')}>
          <span className="not-expired-text">Đăng ký khóa IELTS tại DOL</span>
        </div>
        <div className="action">
          <ActionBtn className="expired">
            <a
              href="https://m.me/dolenglish.ieltsdinhluc"
              target="_blank"
              rel="noopener noreferrer"
              className="action-link"
            >
              <Typography.Text variant="semi-bold/14" color="white100">
                Inbox DOL
              </Typography.Text>
            </a>
          </ActionBtn>
        </div>

        <div
          aria-hidden
          onClick={() => {
            closeBanner(true);
          }}
          className="btn-close"
        >
          <IconOtherClose />
        </div>
      </div>
    </RegisterDOLCourseBannerWrapper>
  );
};

export const ReferralShareBanner = observer(({ className }: any) => {
  const { isLoggedIn } = useGetAuthInfo();
  const { query } = useGetReferralCode();
  const data = query?.data?.data || {};
  const [isClosed, closeBanner] = React.useState(false);
  const isExpired = data?.dpStatus === 'EXPIRED';

  const renderContent = React.useCallback(() => {
    if (isLoggedIn) {
      return (
        <div className="container">
          <div className={cl('content', { expired: isExpired })}>
            {isExpired ? (
              <span className="inline expired-text">
                Tài khoản DOL Pro của bạn hết hạn, gia hạn ngay nhé !!!
              </span>
            ) : (
              <span className="not-expired-text">
                Tìm hiểu cách nhận gói <Red>DOL Pro miễn phí</Red>
              </span>
            )}
          </div>
          <div className="action">
            <ActionBtn className={cl({ expired: isExpired })}>
              <Link
                prefetch={false}
                href={
                  isExpired
                    ? `${routingUrls.landing.STUDENT_VIEW_MY_ACCOUNT}`
                    : `${routingUrls.landing.STUDENT_VIEW_MY_REWARDS}`
                }
                target="_blank"
                rel="nofollow"
              >
                <Typography.Text variant="semi-bold/14" color="white100">
                  {isExpired ? 'Gia hạn' : 'Tìm hiểu'}
                </Typography.Text>
              </Link>
            </ActionBtn>
          </div>

          <div
            aria-hidden
            onClick={() => {
              closeBanner(true);
            }}
            className="btn-close"
          >
            <IconOtherClose />
          </div>
        </div>
      );
    }

    return (
      <div className="container">
        <div className={cl('content', { expired: isExpired })}>
          <span className="not-expired-text">
            Đăng ký tài khoản, trải nghiệm tính năng <Red>premium miễn phí</Red>
          </span>
        </div>
        <div className="action">
          <ActionButtonA />
        </div>

        <div
          aria-hidden
          onClick={() => {
            closeBanner(true);
          }}
          className="btn-close"
        >
          <IconOtherClose />
        </div>
      </div>
    );
  }, [isExpired, isLoggedIn]);

  useBannerScroll({
    bannerId: BANNER_ID,
  });
  React.useEffect(() => {
    if (isLoggedIn) {
      const elm = document.getElementsByClassName('landing-section-middle')?.[0];
      toggleReferralBannerClass(elm, isClosed);
    }
  }, [isClosed, isLoggedIn]);

  if (data?.everPurchased && !isExpired) {
    return <RegisterDOLCourseBanner className={className} closeBanner={closeBanner} />;
  }

  return (
    <Main
      id={BANNER_ID}
      className={cl(BANNER_ID, 'hide', className, { closed: isClosed }, { expired: isExpired })}
    >
      {renderContent()}
    </Main>
  );
});
